import React, { FC, forwardRef, ReactElement, ReactNode } from 'react';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import { StackItem } from '#components/stack-layout/stack-item/stack-item.tsx';
import {
  GDSHeadingLevel,
  HeadingText,
} from '#components/typography/heading-text/heading-text.tsx';
import { styled } from '../../../theme';
import { useThemeStrings } from '../../../hooks/use-theme';
import type {
  GDSCustomizableComponent,
  GDSComponent,
  GDSStatus,
  GDSIcon,
  GDSEmoji,
} from '../../../types';
import { Icon, type GDSIconSource } from '../../icon/icon';
import { Button } from '../../button/button';
import {
  backgroundColorByStatus,
  iconTintByStatus,
} from '../status-color-tokens';
import { GenesisCoreInspector } from '../../../test-utils/genesis-core-inspector';
import { checkIsTextAction } from '../../utils/check-is-textaction';

export interface GDSStatusBannerProps
  extends GDSCustomizableComponent,
    GDSComponent {
  status: GDSStatus;
  title?: React.ReactNode;
  showIcon?: boolean;
  onDismissClick?: (event: React.MouseEvent) => void;
  headingLevel?: GDSHeadingLevel;
  icon?: GDSIconSource | ReactElement<GDSIcon | GDSEmoji>;
  iconLabel?: string;
  children?: ReactNode;
  textAction?: ReactNode;
}

const DISPLAY_NAME = 'StatusBanner';

const iconByStatus: Record<GDSStatus, GDSIconSource> = {
  success: 'statusSuccessStatusFilled',
  error: 'statusErrorStatusFilled',
  info: 'statusInformationCircleFill',
  warning: 'statusAlertFill',
  neutral: 'statusInformationCircleFill',
  brand: 'descriptivePerson',
};

const StatusBannerBase = styled(StackLayout, {
  borderRadius: '$small',
  padding: '$one',
  variants: {
    status: {
      info: {
        backgroundColor: backgroundColorByStatus.info,
      },
      success: {
        backgroundColor: backgroundColorByStatus.success,
      },
      warning: {
        backgroundColor: backgroundColorByStatus.warning,
      },
      error: {
        backgroundColor: backgroundColorByStatus.error,
      },
      brand: {
        backgroundColor: backgroundColorByStatus.brand,
      },
      neutral: {
        backgroundColor: backgroundColorByStatus.neutral,
      },
    },
  },
});

export const StatusBanner: FC<GDSStatusBannerProps> = forwardRef(
  (
    {
      status,
      title,
      showIcon = true,
      headingLevel = 'display',
      icon,
      iconLabel,
      onDismissClick,
      textAction,
      css,
      children,
      className,
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      ...props
    }: GDSStatusBannerProps,
    ref: React.Ref<HTMLDivElement>,
  ) => {
    const hasChildren = Boolean(children);
    const strings = useThemeStrings();

    const isTextAction = textAction ? checkIsTextAction(textAction) : undefined;

    if (textAction && onDismissClick) {
      console.warn(
        'StatusBanner only accepts either textAction or onDismissClick. Please remove one of them.',
      );
    }

    // There are some default icons defined based on kind
    const defaultTint = iconTintByStatus[status];
    const defaultIcon = iconByStatus[status];

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <StatusBannerBase
          className={['GDS-status-banner', className].join(' ')}
          orientation="horizontal"
          spacing="$half"
          verticalAlignment={hasChildren ? undefined : 'center'}
          status={status}
          ref={ref}
          css={css}
          data-testid={testId}
          {...props}
        >
          {showIcon ? (
            <StackItem>
              <Icon
                icon={icon || defaultIcon}
                tint={defaultTint}
                className={className}
                label={iconLabel}
                css={{ typography: '$subtitleOne' }}
              />
            </StackItem>
          ) : null}
          <StackItem growFactor={1}>
            <StackLayout>
              {title ? (
                <StackItem>
                  {React.isValidElement(title) ? (
                    title
                  ) : (
                    <HeadingText
                      level={headingLevel}
                      size="sm"
                      className="GDS-status-banner-title"
                    >
                      {title}
                    </HeadingText>
                  )}
                </StackItem>
              ) : null}
              {children ? <StackItem>{children}</StackItem> : null}
            </StackLayout>
          </StackItem>
          {isTextAction && textAction}
          {onDismissClick && !textAction ? (
            <StackItem>
              <Button
                className="GDS-status-banner-dismiss-button"
                onClick={onDismissClick}
                priority="tertiary"
                quiet
                hideLabel
                icon="tinyClose"
                size="small"
              >
                {strings?.dismiss}
              </Button>
            </StackItem>
          ) : null}
        </StatusBannerBase>
      </GenesisCoreInspector>
    );
  },
);

StatusBanner.displayName = DISPLAY_NAME;
